import React, { useEffect, useState } from 'react';

import Container from '@mui/material/Container';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import { PageTab } from 'app/routes/super-user/tabs'
import { EntityOfficerInvitationsPage } from 'legal-entities/pages';
import SuperUserAPIService from 'users/entities/super-user/api/super-user-api-service';
import SystemNotifications from 'users/entities/super-user/view/system-notifications';
import UsersAdmin from 'users/entities/super-user/view/users';
import ActiveUserInvitationsPage from 'users/entities/super-user/view/active-user-invitations-page';
import axios, { CanceledError } from 'axios';
import { useSession } from 'users/session/session-context';
import { useLoaderData, useNavigate } from "react-router-dom";
import Guid from "common/values/guid/guid";
import ViewCompanyProfile from "marketplace/values/company-profile/view/view-company-profile";
import { useDialog } from "app/providers/dialog";
import { NotAuthorized } from 'common/components/not-authorized';

const PageContainer = styled(Container)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    padding: 0
  },
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  marginTop: theme.spacing(6),
  maxWidth: '70rem'
}));
const PageTitle = styled(Typography)({
  marginBottom: '2rem',
  textAlign: 'center'
});
const Version = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[500],
  fontSize: '1.2em',
  marginTop: theme.spacing(2),
  textAlign: 'left'
}));

export default function SuperUserDashboard() {
  const routeParams = useLoaderData();
  const [activeTab, setActiveTab] = React.useState<PageTab>(PageTab.Users);
  const [appVersion, setAppVersion] = useState<string>('Unknown');

  const session = useSession();
  const navigate = useNavigate();
  const { openDialog } = useDialog();

  useEffect(() => {
    const tab = Object.values(PageTab).find((tab) => tab === routeParams.tab);
    if (tab) {
      setActiveTab(tab);
    } else {
      navigate(`/super-user/${PageTab.Users}`);
      return;
    }

    loadRouteProfile(routeParams.type, routeParams.id);

    function loadRouteProfile(type?: string, id?: string) {
      if (!type || !id) return;

      const guidId = new Guid(id);
      const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);

      if (type === 'company') {
        openDialog({
          component: <ViewCompanyProfile companyId={guidId} />,
          MuiProps: {
            fullWidth: true,
            maxWidth: 'lg'
          },
          contentSxProps: {
            display: "flex",
          },
          titleStyle: {
            position: "absolute",
            right: 0,
            top: 0,
          }
        },
          () => navigate(parentPath.pathname)

        );
      } else {
        console.warn(`Unknown profile type: ${type}`);
      }
    }
  }, [routeParams, activeTab]);

  useEffect(() => {
    if (!session.user?.isSuperUser) return;

    async function getAppVersion(abortController: AbortController): Promise<string | undefined> {
      try {
        const superUserService = new SuperUserAPIService(session);
        return await superUserService.getAppVersion(abortController);
      } catch (error) {
        if (error instanceof CanceledError) return;
        if (axios.isAxiosError(error)) console.error(error.message);
      }
    }
    let abortController = new AbortController();

    getAppVersion(abortController).then((version) => setAppVersion(version ?? 'Unknown'));
    return () => {
      abortController.abort();
      abortController = new AbortController();
    };
  }, []);

  if (!session.user?.isSuperUser) return <NotAuthorized />;

  return (
    <PageContainer>
      <PageTitle id="super-user-dashboard-header" variant="h4">Super User Dashboard</PageTitle>
      <Tabs
        variant="fullWidth"
        indicatorColor="primary"
        value={activeTab}
        onChange={(event: React.SyntheticEvent, newValue: string) => navigate(`/super-user/${newValue}`)}
        textColor="primary">
        <Tab id="super-user-users-tab" label="Users" value="users" />
        <Tab id="super-user-entities-tab" label="Entities" value="entities" />
        <Tab id="super-user-invitations-tab" label="Invitations" value="invitations" />
        <Tab id="super-user-notifications-tab" label="System Notifications" value="system-notifications" />
      </Tabs>
      {activeTab === PageTab.Users && (
        <UsersAdmin
          onProfileView={(type?: string, id?: string) => {
            if (!type || !id) return;
            navigate(`/super-user/${PageTab.Users}/${type}/${id}`);
          }} />
      )}
      {activeTab === PageTab.Entities && (
        <EntityOfficerInvitationsPage />
      )}
      {activeTab === PageTab.Invitations && (
        <ActiveUserInvitationsPage />
      )}
      {activeTab === PageTab.SystemNotifications && (
        <SystemNotifications />
      )}
      <Version>{appVersion}</Version>
    </PageContainer>
  )
}